<template>
    <div>
        <v-row v-for="parameter in parameters" :key="parameter.id" align="center">
            <v-col lg="3" cols="4">
                {{ parameter.name }}:
            </v-col>
            <v-col cols="8" lg="9">
                <div class="colors" v-if="parameter.show_as === 'colors'">
                    <v-btn-toggle
                        v-model="selected[parameter.id]"
                        :disabled="disabled"
                        class="h-auto flex-wrap color-wrap ml-n2 pr-2 pb-2"
                        @update:modelValue="changeOption(parameter.id, $event)"
                    >
                        <v-btn
                            v-for="value in usableValues[parameter.id]" :key="value"
                            :icon="mdiCheck"
                            class="color-btn pa-0 mt-2 ml-2"
                            :class="{'glob-disable': disabled}"
                            :style="{backgroundColor: parameter.values[value].parameters_values.show_as || 'white'}"
                            :value="value"
                            :disabled="(filterOut[parameter.id] && !filterOut[parameter.id].includes(value)) || (!filterOut[parameter.id] && selected.length)"
                        />
                    </v-btn-toggle>
                </div>
                <div class="select d-flex d-lg-block" v-if="parameter.show_as === 'select'">
                    <v-select
                        :disabled="disabled"
                        hide-details
                        density="comfortable"
                        variant="outlined"
                        class="w-50"
                        clearable
                        v-model="selected[parameter.id]"
                        :placeholder="$t('eshop::variant-picker.choose')"
                        :items="selectItems[parameter.id]"
                        @update:modelValue="changeOption(parameter.id, $event)"
                    >
                        <template v-slot:item="{ props, item }">
                            <v-list-item
                                v-bind="props"
                                :prepend-avatar="item.raw.avatar"
                                :title="item.raw.title"
                                :disabled="item.raw.disabled"
                            ></v-list-item>
                        </template>
                    </v-select>
                </div>
                <div class="select d-flex d-lg-block" v-if="parameter.show_as === 'buttons'">
                    <v-chip-group v-model="selected[parameter.id]"
                                  :disabled="disabled"
                                  column
                                  @update:modelValue="changeOption(parameter.id, $event)"
                    >
                        <v-chip
                            v-for="value in usableValues[parameter.id]" :key="value"
                            :text="parameter.values[value].parameters_values.show_as || parameter.values[value].parameters_values.name"
                            :value="value"
                            variant="outlined"
                            rounded="lg"
                            filter
                            :disabled="(filterOut[parameter.id] && !filterOut[parameter.id].includes(value)) || (!filterOut[parameter.id] && selected.length)"
                        ></v-chip>
                    </v-chip-group>

                </div>
            </v-col>
        </v-row>
    </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { mdiCheck } from '@mdi/js';

const props = defineProps({
    variants: [Array, Object],
    usableValues: Object,
    parameters: Object,
});
const disabled = ref(false);
const filterOut = ref({});
const selected = ref({});
if (props.variants.id) {
    disabled.value = true;
    for (let i = 0; i < props.variants.options.length; i++) {
        selected.value[props.variants.options[i].id_parameters] = props.variants.options[i].id_products_parameters_values;
    }
}
const selectedVariant = ref(null);
const selectItems = computed(() => {
    const obj = {};
    for (const [key, value] of Object.entries(props.parameters)) {
        if (value.show_as === 'select') {
            obj[key] = getItems(key);
        }
    }
    return obj;
});

const parameters = computed(() => {
    const params = {};
    for (const paramsKey in props.parameters) {
        if (paramsKey in props.usableValues) {
            params[paramsKey] = props.parameters[paramsKey];
        }
    }
    return params;
});

const selectedValues = computed(() => Object.entries(selected.value));
const paramsCount = computed(() => Object.entries(props.parameters).length);
const selectedLength = computed(() => selectedValues.value.filter(([item, item_val]) => item_val !== undefined && item_val !== null).length);
const getItems = (paramId) => {
    const items = [];
    if (props.usableValues[paramId]?.length) {
        for (let i = 0; i < props.usableValues[paramId].length; i++) {
            const val = props.parameters[paramId].values[props.usableValues[paramId][i]];
            if (!val) {
                continue;
            }
            items.push({
                title: val.value ||
                    val.parameters_values.show_as ||
                    val.parameters_values.name,
                value: props.usableValues[paramId][i],
                sort: val.parameters_values.sort,
                disabled: filterOut.value[paramId] && !filterOut.value[paramId].includes(props.usableValues[paramId][i]),
            });
        }
    }
   return items.sort((a, b) => a.sort - b.sort);
};
const changeOption = (paramId) => {
    filterOut.value = {};
    if (selectedLength.value && paramsCount.value > 1) {
        const values = Object.values(selected.value).filter((val) => !!val);
        const prepared = props.variants.filter((item) => {
            return values.every(e => item.options.map((opt) => opt.id_products_parameters_values).includes(e));
        });
        // console.log(values);
        // console.log(prepared);
        for (let i = 0; i < prepared.length; i++) {
            for (let j = 0; j < prepared[i].options.length; j++) {
                const _key = prepared[i].options[j].id_parameters;
                const _val = prepared[i].options[j].id_products_parameters_values;
                if (filterOut.value[_key]) {
                    if (!filterOut.value[_key].includes(_val)) {
                        filterOut.value[_key].push(_val);
                    }
                } else {
                    filterOut.value[_key] = [_val];
                }
            }
        }
    }
};
const emit = defineEmits(['variant-found']);
watch(selected.value, (val) => {
    const values = Object.entries(val);
    if (selectedLength.value && paramsCount.value === selectedLength.value) {
        for (let i = 0; i < props.variants.length; i++) {
            let foundCount = 0;
            for (const [innerkey, innerval] of values) {
                if (props.variants[i].options.filter((item) => {
                    return item.id_parameters == innerkey && item.id_products_parameters_values === innerval;
                }).length) {
                    foundCount++;
                }
            }
            if (foundCount === paramsCount.value) {
                selectedVariant.value = props.variants[i].id;
                emit('variant-found', props.variants[i].id);
                return;
            }
        }
        selectedVariant.value = null;
    } else {
        selectedVariant.value = null;
    }
    emit('variant-found', null);
});
</script>

<style scoped lang="scss">
@use 'vuetify/lib/styles/settings/colors' as *;
@use 'sass:map';

.color-wrap {
    .v-btn:first-child {
        border-start-start-radius: 50%;
        border-end-start-radius: 50%;
    }

    .v-btn--disabled {
        box-shadow: 0 0 0 .1em white, 0 0 0 0.2em map.get($red, base);
    }

    .v-btn:last-child {
        border-start-end-radius: 50%;
        border-end-end-radius: 50%;
    }
}

::v-deep(.color-btn) {
    width: 1.8em !important;
    height: 1.8em !important;
    min-width: 1.8em;
    border-radius: 50%;
    box-shadow: 0 0 .1em grey;

    .v-btn__content {
        color: white !important;
        opacity: 0;
    }

    &.v-btn--active {
        box-shadow: 0 0 0 .2em white, 0 0 0 .3em map.get($green, base);

        .v-btn__overlay {
            opacity: 0.04 !important;
        }

        .v-btn__content {
            opacity: 1;
        }
    }
}

</style>
